import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Lottie from 'lottie-react';
import brochureAnim from '../animationbrochure.json';
import '../csscomponent/Brochure.css';
import { getCmsSettings } from '../lib/sqlAuth';
import { subscribeCmsUpdates } from '../lib/cmsSync';

export default function Brochure() {
  const [showPreview, setShowPreview] = useState(false);
  const [content, setContent] = useState({
    tag: 'Company Brochure',
    title: 'Everything you need before we start building.',
    summary: 'Download the brochure to review our capabilities, process, engagement model, and expected outcomes.',
    pdfUrl: `${process.env.PUBLIC_URL}/company-brochure.pdf`,
    points: [
      'Service packages and estimated delivery windows',
      'Product development lifecycle and communication model',
      'Support plans and maintenance options',
      'Technology stack and handover standards',
      'Pricing, scope, and delivery model for startup to enterprise needs',
    ],
  });
  const closePreview = () => setShowPreview(false);

  useEffect(() => {
    if (!showPreview) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') setShowPreview(false);
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [showPreview]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await getCmsSettings({ keys: ['brochure_content'] });
        if (!mounted) return;
        const next = res?.settings?.brochure_content || {};
        setContent((prev) => ({ ...prev, ...next }));
      } catch (_) {}
    };
    load();
    const unsubscribe = subscribeCmsUpdates((payload) => {
      if (!payload?.scope || ['site', 'brochure'].includes(payload.scope)) {
        load();
      }
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  return (
    <section className="brochure-page">
      <motion.div
        className="brochure-box"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="brochure-top">
          <div>
            <p className="brochure-tag">{content.tag}</p>
            <h1>{content.title}</h1>
            <p className="brochure-summary">{content.summary}</p>
            <div className="brochure-highlight-row">
              <div className="brochure-highlight-card">
                <strong>Startup to Enterprise</strong>
                <span>Clear delivery scope for websites, dashboards, and product builds.</span>
              </div>
              <div className="brochure-highlight-card">
                <strong>Process + Pricing</strong>
                <span>See packages, timelines, handover flow, and support commitments in one place.</span>
              </div>
            </div>
          </div>
          <div className="brochure-lottie-wrap">
            <Lottie animationData={brochureAnim} loop className="brochure-lottie" />
          </div>
        </div>

        <div className="brochure-content-grid">
          <div>
            <h3 className="brochure-subtitle">What You Will Find</h3>
            <ul>
              {(Array.isArray(content.points) ? content.points : []).map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </div>
          <div className="brochure-side-card">
            <span className="brochure-side-kicker">Inside the brochure</span>
            <h3>Built to help clients decide faster</h3>
            <p>
              Review capabilities, package options, collaboration model, launch support, and the quality standards
              we follow before project kickoff.
            </p>
            <div className="brochure-stat-row">
              <div>
                <strong>3+</strong>
                <span>package tiers</span>
              </div>
              <div>
                <strong>24/7</strong>
                <span>support view</span>
              </div>
              <div>
                <strong>100%</strong>
                <span>clear scope</span>
              </div>
            </div>
          </div>
        </div>

        <div className="brochure-actions">
          <button
            type="button"
            className="brochure-btn primary"
            onClick={() => setShowPreview(true)}
          >
            Preview Brochure
          </button>
          <a href={content.pdfUrl} download className="brochure-btn secondary">
            Download PDF
          </a>
        </div>
      </motion.div>

      {showPreview ? (
        <div className="brochure-modal" onClick={closePreview}>
          <div className="brochure-modal-content" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="brochure-modal-close"
              aria-label="Close brochure preview"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                closePreview();
              }}
            >
              X
            </button>
            <iframe title="Company Brochure Preview" src={content.pdfUrl} className="brochure-preview-frame" />
          </div>
        </div>
      ) : null}
    </section>
  );
}
